import { makeVar, useReactiveVar } from '@apollo/client'

const STORAGE_KEY_PREFIX = 'flatTireSimulation:'
const WORK_ORDER_SEEN_KEY_PREFIX = 'flatTireWorkOrderSeen:'

export const FLAT_TIRE_STORAGE_KEY_PREFIX = STORAGE_KEY_PREFIX

// ─── Simulation state ──────────────────────────────────────────────────────

const loadSimulationsFromStorage = (): Map<string, string> => {
  const map = new Map<string, string>()
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith(STORAGE_KEY_PREFIX)) {
      map.set(key.replace(STORAGE_KEY_PREFIX, ''), localStorage.getItem(key)!)
    }
  }
  return map
}

const flatTireSimulationsVar = makeVar<Map<string, string>>(
  loadSimulationsFromStorage(),
)

export const triggerFlatTireSimulation = (vehicleId: string): void => {
  const iso = new Date().toISOString()
  localStorage.setItem(`${STORAGE_KEY_PREFIX}${vehicleId}`, iso)
  localStorage.removeItem(`${WORK_ORDER_SEEN_KEY_PREFIX}${vehicleId}`)
  const next = new Map(flatTireSimulationsVar())
  next.set(vehicleId, iso)
  flatTireSimulationsVar(next)
}

export const clearFlatTireSimulation = (vehicleId: string): void => {
  localStorage.removeItem(`${STORAGE_KEY_PREFIX}${vehicleId}`)
  localStorage.removeItem(`${WORK_ORDER_SEEN_KEY_PREFIX}${vehicleId}`)
  const next = new Map(flatTireSimulationsVar())
  next.delete(vehicleId)
  flatTireSimulationsVar(next)
}

export const useFlatTireSimulationActive = (vehicleId: string): boolean => {
  const simulations = useReactiveVar(flatTireSimulationsVar)
  return simulations.has(vehicleId)
}

export const useFlatTireSimulatedVehicleIds = (): Set<string> => {
  const simulations = useReactiveVar(flatTireSimulationsVar)
  return new Set(simulations.keys())
}

export const useFlatTireSimulationCreatedAt = (
  vehicleId: string,
): Date | null => {
  const simulations = useReactiveVar(flatTireSimulationsVar)
  const iso = simulations.get(vehicleId)
  return iso ? new Date(iso) : null
}

// ─── Work order seen state ─────────────────────────────────────────────────

const loadWorkOrderSeenFromStorage = (): Set<string> => {
  const set = new Set<string>()
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith(WORK_ORDER_SEEN_KEY_PREFIX)) {
      set.add(key.replace(WORK_ORDER_SEEN_KEY_PREFIX, ''))
    }
  }
  return set
}

const workOrderSeenVar = makeVar<Set<string>>(loadWorkOrderSeenFromStorage())

export const markFlatTireWorkOrderSeen = (vehicleId: string): void => {
  localStorage.setItem(`${WORK_ORDER_SEEN_KEY_PREFIX}${vehicleId}`, '1')
  const next = new Set(workOrderSeenVar())
  next.add(vehicleId)
  workOrderSeenVar(next)
}

export const useWasFlatTireWorkOrderSeen = (vehicleId: string): boolean => {
  const seen = useReactiveVar(workOrderSeenVar)
  return seen.has(vehicleId)
}
